import { Wallet } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, useUser } from "@/auth/AuthContext";
import { useQuickAdd } from "@/components/AppShell";
import { IncomeExpenseChart } from "@/components/charts/Charts";
import { BudgetMeter, ShareBars, StatTile } from "@/components/charts/Figures";
import { Button } from "@/components/ui/Button";
import { EmptyState, ErrorState, Spinner, StatusText } from "@/components/ui/Feedback";
import { PageHeader, Panel } from "@/components/ui/Layout";
import { Money } from "@/components/ui/Money";
import { MonthPicker } from "@/components/ui/MonthPicker";
import { formatDate, formatPercent, formatPeriod } from "@/lib/format";
import { addCycles, cycleStart, isCalendarMonth, periodContaining } from "@/lib/period";
import { isNetworkError, useDashboard } from "@/lib/queries";
import { TransactionRow } from "../transactions/TransactionRow";
import { OfflineOverview } from "./OfflineOverview";

export function DashboardPage() {
  const user = useUser();
  const { today } = useAuth();
  const quickAdd = useQuickAdd();
  const [params, setParams] = useSearchParams();
  const startDay = user.financial_cycle_start_day;
  const currentPeriod = periodContaining(today, startDay).start;
  const month = params.get("month") ?? currentPeriod;
  const period = periodContaining(month, startDay);
  const periodLabel = formatPeriod(period.start, period.end, isCalendarMonth(period));
  const { data, error, isPending, isFetching, refetch } = useDashboard(month);

  const header = (
    <PageHeader
      title={`Hello${user.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}`}
      subtitle={data?.period.is_current_month ? `Month to date · ${data.period.days_left} days left` : periodLabel}
      actions={
        <MonthPicker
          month={month}
          max={currentPeriod}
          label={(m) => { const p = periodContaining(m, startDay); return formatPeriod(p.start, p.end, isCalendarMonth(p)); }}
          shift={(m, delta) => addCycles(cycleStart(m, startDay), delta, startDay)}
          onChange={(m) => setParams(m === currentPeriod ? {} : { month: m }, { replace: true })}
        />
      }
    />
  );

  if (isPending) return <>{header}<Spinner /></>;
  if (error) {
    // No connection is not a failure to be reported: show what the device holds instead.
    if (isNetworkError(error)) return <>{header}<OfflineOverview /></>;
    return <>{header}<ErrorState error={error} onRetry={() => void refetch()} /></>;
  }
  const d = data;
  const cur = d.currency;

  if (d.accounts.length === 0) {
    return (
      <>
        {header}
        <EmptyState
          icon={<Wallet className="size-8" />}
          title="Add your first account"
          body="Start with where your money lives — M-Pesa, a bank account or cash. Balances are calculated from your transactions."
          action={<Link to="/accounts?new=1"><Button variant="primary">Add an account</Button></Link>}
        />
      </>
    );
  }

  const m = d.month;
  return (
    <div className={isFetching ? "opacity-90" : undefined}>
      {header}

      <section aria-label="Position" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Net worth" emphasis className="col-span-2 lg:col-span-1" value={<Money value={d.position.net_worth} currency={cur} />}
                  note={d.excluded_currencies.length > 0
                    ? `Excludes ${d.excluded_currencies.join(", ")} accounts` : "Assets minus liabilities"} />
        <StatTile label="Income" value={<Money value={m.income} currency={cur} />}
                  note={d.period.is_current_month ? "This month so far" : periodLabel} />
        <StatTile label="Expenses" value={<Money value={m.expense} currency={cur} />}
                  note="Net of refunds" />
        <StatTile
          label="Net cash flow"
          className="col-span-2 lg:col-span-1"
          value={<Money value={m.net_cash_flow} currency={cur} signed />}
          note={m.savings_rate !== null ? `Savings rate ${formatPercent(m.savings_rate)}` : "No income yet"}
        />
      </section>

      <section aria-label="Balances" className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Total assets" value={<Money value={d.position.total_assets} currency={cur} />} />
        <StatTile label="Total liabilities" value={<Money value={d.position.total_liabilities} currency={cur} />} />
        <StatTile
          label="Budget used"
          value={m.budget_utilisation !== null ? formatPercent(m.budget_utilisation) : "—"}
          note={Number(m.budgeted) > 0
            ? <>of <Money value={m.budgeted} currency={cur} /> budgeted</>
            : <Link to="/budgets" className="text-accent hover:underline">Set a budget</Link>}
        />
        <StatTile
          label="Budgets needing attention"
          value={d.budgets.over + d.budgets.warning}
          note={d.budgets.count ? `${d.budgets.over} over · ${d.budgets.warning} close` : "No budgets this month"}
        />
      </section>

      <div className="mt-4">
        <IncomeExpenseChart data={d.trend} currency={cur} title="Income vs expenses, last 12 months"
                            loading={isFetching} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Spending by category" action={<Link to="/reports?tab=expenses" className="text-xs font-medium text-accent hover:underline">Details</Link>}>
          <ShareBars rows={d.spending_by_category} currency={cur} empty="No spending recorded this month." />
        </Panel>
        <Panel title="Income by source">
          <ShareBars rows={d.income_by_source} currency={cur} empty="No income recorded this month." />
        </Panel>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Accounts" className="lg:col-span-2" bodyClassName="p-0"
               action={<Link to="/accounts" className="text-xs font-medium text-accent hover:underline">Manage</Link>}>
          <ul>
            {d.accounts.filter((a) => a.is_active).map((a) => (
              <li key={a.id} className="border-t border-line first:border-t-0">
                <Link to={`/accounts/${a.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-2">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-ink">{a.name}</span>
                    <span className="text-xs capitalize text-ink-3">{a.account_type.replace("_", " ")} · {a.currency}</span>
                  </span>
                  <Money value={a.balance} currency={a.currency} className="font-medium" />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Top merchants">
          {d.top_merchants.length === 0 ? (
            <p className="text-sm text-ink-3">Add a merchant to expenses to see where money goes.</p>
          ) : (
            <ol className="flex flex-col gap-2.5">
              {d.top_merchants.map((mch) => (
                <li key={mch.merchant_id} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="truncate text-ink">{mch.name}
                    <span className="ml-1.5 text-xs text-ink-3">{mch.count}×</span></span>
                  <Money value={mch.amount} currency={cur} />
                </li>
              ))}
            </ol>
          )}
          {d.budgets.count > 0 && (
            <div className="mt-5 border-t border-line pt-4">
              <p className="mb-2 text-xs font-medium text-ink-3">Budget</p>
              <BudgetMeter
                percent={m.budget_utilisation}
                status={d.budgets.over > 0 ? "over" : d.budgets.warning > 0 ? "warning" : "on_track"}
                statusText={d.budgets.over > 0
                  ? `${d.budgets.over} budget${d.budgets.over > 1 ? "s" : ""} over`
                  : d.budgets.warning > 0 ? `${d.budgets.warning} close to the limit` : "On track"}
              />
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Largest transactions" className="mt-4" bodyClassName="p-0"
             action={<Link to={`/transactions?date_from=${d.period.date_from}&date_to=${d.period.date_to}&sort=-amount`} className="text-xs font-medium text-accent hover:underline">See all</Link>}>
        {d.largest_transactions.length === 0 ? (
          <div className="p-4">
            <p className="text-sm text-ink-3">Nothing recorded for {periodLabel} yet.</p>
            <Button variant="primary" size="sm" className="mt-3" onClick={() => quickAdd()}>Add a transaction</Button>
          </div>
        ) : (
          <ul>{d.largest_transactions.map((t) => <TransactionRow key={t.id} txn={t} />)}</ul>
        )}
      </Panel>
      <p className="mt-4 text-xs text-ink-3">
        <StatusText status="info">All figures are calculated by the server from your ledger as of {formatDate(d.period.today)}.</StatusText>
      </p>
    </div>
  );
}
